/**
 * Vendor Bot Admin - Products Page
 * Product list with CRUD operations
 */

import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../../components/Layout';
import Navbar from '../../components/Navbar';
import Card from '../../components/Card';
import Button from '../../components/Button';
import { useTelegram } from '../../hooks/useTelegram';

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--spacing-md);
`;

const ProductCard = styled(Card)`
  display: flex;
  gap: var(--spacing-md);
`;

const ProductImage = styled.div`
  width: 80px;
  height: 80px;
  border-radius: var(--radius-md);
  background: var(--bg-secondary);
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
`;

const ProductInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
`;

const ProductName = styled.div`
  font-size: var(--font-md);
  font-weight: var(--font-semibold);
  color: var(--text-primary);
`;

const ProductPrice = styled.div`
  font-size: var(--font-lg);
  font-weight: var(--font-bold);
  color: var(--accent-green);
`;

const ProductMeta = styled.div`
  font-size: var(--font-sm);
  color: var(--text-secondary);
`;

const ProductStatus = styled.span<{ active: boolean }>`
  padding: var(--spacing-xs) var(--spacing-sm);
  border-radius: var(--radius-sm);
  font-size: var(--font-xs);
  font-weight: var(--font-medium);
  background: ${({ active }) => (active ? 'rgba(76, 175, 80, 0.2)' : 'rgba(142, 142, 147, 0.2)')};
  color: ${({ active }) => (active ? 'var(--accent-green)' : 'var(--text-secondary)')};
`;

const ProductActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
  justify-content: center;
`;

const FilterBar = styled.div`
  display: flex;
  gap: var(--spacing-sm);
  margin-bottom: var(--spacing-lg);
  overflow-x: auto;
  padding-bottom: var(--spacing-sm);
`;

const FilterChip = styled.button<{ active: boolean }>`
  padding: var(--spacing-sm) var(--spacing-md);
  border-radius: var(--radius-md);
  font-size: var(--font-sm);
  font-weight: var(--font-medium);
  white-space: nowrap;
  background: ${({ active }) => (active ? 'var(--accent-green)' : 'var(--bg-card)')};
  color: ${({ active }) => (active ? 'white' : 'var(--text-secondary)')};
  border: 1px solid ${({ active }) => (active ? 'var(--accent-green)' : 'var(--border-color)')};
`;

const Products: React.FC = () => {
  const { botUsername } = useParams<{ botUsername: string }>();
  const navigate = useNavigate();
  const { showMainButton, showBackButton } = useTelegram();
  const [filter, setFilter] = useState<'all' | 'physical' | 'digital' | 'subscription'>('all');
  const [products] = useState([
    {
      id: 1,
      name: 'Premium Course',
      price: 49.99,
      type: 'digital',
      stock: null,
      active: true,
      sales: 45,
    },
    {
      id: 2,
      name: 'T-Shirt',
      price: 25.0,
      type: 'physical',
      stock: 100,
      active: true,
      sales: 23,
    },
    {
      id: 3,
      name: 'Monthly Subscription',
      price: 9.99,
      type: 'subscription',
      stock: null,
      active: true,
      sales: 67,
    },
  ]);

  useEffect(() => {
    showMainButton('Add Product', () => navigate(`/vendor/${botUsername}/admin/product/new`));
    showBackButton(() => navigate(`/vendor/${botUsername}/admin/dashboard`));
  }, [showMainButton, showBackButton, navigate, botUsername]);

  const filteredProducts = filter === 'all' ? products : products.filter((p) => p.type === filter);

  return (
    <Layout header={<Navbar title="Products" showBack />}>
      <FilterBar>
        <FilterChip active={filter === 'all'} onClick={() => setFilter('all')}>
          All Products
        </FilterChip>
        <FilterChip active={filter === 'physical'} onClick={() => setFilter('physical')}>
          📦 Physical
        </FilterChip>
        <FilterChip active={filter === 'digital'} onClick={() => setFilter('digital')}>
          💾 Digital
        </FilterChip>
        <FilterChip active={filter === 'subscription'} onClick={() => setFilter('subscription')}>
          🔄 Subscription
        </FilterChip>
      </FilterBar>

      <ProductGrid>
        {filteredProducts.map((product) => (
          <ProductCard key={product.id}>
            <ProductImage>
              {product.type === 'digital' ? '💾' : product.type === 'subscription' ? '🔄' : '📦'}
            </ProductImage>
            <ProductInfo>
              <ProductName>{product.name}</ProductName>
              <ProductPrice>${product.price.toFixed(2)}</ProductPrice>
              <ProductMeta>
                {product.stock !== null ? `Stock: ${product.stock}` : 'Digital'} • {product.sales} sales
              </ProductMeta>
              <ProductStatus active={product.active}>{product.active ? 'Active' : 'Inactive'}</ProductStatus>
            </ProductInfo>
            <ProductActions>
              <Button
                size="small"
                onClick={() => navigate(`/vendor/${botUsername}/admin/product/${product.id}`)}
              >
                ✏️
              </Button>
            </ProductActions>
          </ProductCard>
        ))}
      </ProductGrid>
    </Layout>
  );
};

export default Products;
